import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronRight, ArrowUp, ArrowDown, Trash2, Send, Undo2, Sparkles, Loader2, Save, Layers, FileDown, Lock, LockOpen } from 'lucide-react';
import { Switch } from '../ui/switch';
import { downloadModuleNotes } from '../../lib/studyGuide';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import RichTextEditor from './RichTextEditor';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

const toForm = (m) => ({
  title: m.title || '',
  overview_html: m.overview_html || '',
  outcomes: (m.learning_outcomes || []).join('\n'),
  start_date: m.start_date || '',
  end_date: m.end_date || '',
});

function ModuleOverviewEditor({ module, programmeId, token, onSaved }) {
  const [form, setForm] = useState(toForm(module));
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { setForm(toForm(module)); }, [module.module_id, module.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/programmes/${programmeId}/modules/${module.module_id}`, {
        title: form.title.trim() || module.title,
        overview_html: form.overview_html,
        learning_outcomes: form.outcomes.split('\n'),
        start_date: form.start_date,
        end_date: form.end_date,
      }, { headers });
      toast.success('Module saved');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  const draft = async () => {
    setDrafting(true);
    try {
      const res = await axios.post(`${API}/programmes/${programmeId}/modules/${module.module_id}/draft-overview`, {}, { headers, timeout: 120000 });
      setForm((f) => ({ ...f, overview_html: res.data.overview_html || f.overview_html, outcomes: (res.data.learning_outcomes || []).join('\n') || f.outcomes }));
      toast.success('Draft ready — review it, then click Save module');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'NAVI could not draft the overview');
    } finally {
      setDrafting(false);
    }
  };

  return (
    <div className="p-4 space-y-4 border-t border-zinc-800" data-testid={`module-overview-editor-${module.module_id}`}>
      <div className="grid md:grid-cols-3 gap-3">
        <div className="md:col-span-1">
          <Label className="text-zinc-400 text-xs">Module title</Label>
          <Input value={form.title} onChange={(e) => set('title', e.target.value)} className="mt-1 bg-zinc-800 border-zinc-700 text-white" data-testid={`module-title-input-${module.module_id}`} />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs">Start date</Label>
          <Input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} className="mt-1 bg-zinc-800 border-zinc-700 text-white" data-testid={`module-start-${module.module_id}`} />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs">End date</Label>
          <Input type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} className="mt-1 bg-zinc-800 border-zinc-700 text-white" data-testid={`module-end-${module.module_id}`} />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-zinc-400 text-xs">Module overview (shown to learners)</Label>
          <Button type="button" size="sm" variant="ghost" onClick={draft} disabled={drafting} className="h-7 text-purple-300 hover:text-purple-200 hover:bg-purple-500/10" data-testid={`module-draft-ai-${module.module_id}`}>
            {drafting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
            {drafting ? 'Drafting…' : 'Draft with NAVI AI'}
          </Button>
        </div>
        <RichTextEditor value={form.overview_html} onChange={(html) => set('overview_html', html)} placeholder="What this module covers and why it matters..." />
      </div>
      <div>
        <Label className="text-zinc-400 text-xs">Learning outcomes (one per line)</Label>
        <Textarea rows={4} value={form.outcomes} onChange={(e) => set('outcomes', e.target.value)} className="mt-1 bg-zinc-800 border-zinc-700 text-white" placeholder="Explain…&#10;Distinguish…" data-testid={`module-outcomes-${module.module_id}`} />
      </div>
      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid={`module-save-${module.module_id}`}>
          <Save className="h-4 w-4 mr-1.5" />{saving ? 'Saving…' : 'Save module'}
        </Button>
      </div>
    </div>
  );
}

function ModuleLockToggle({ module, programmeId, token, onReload }) {
  const [busy, setBusy] = useState(false);
  const locked = !!module.locked;
  const toggle = async () => {
    const drafts = (module.activity_count || 0) - (module.published_count || 0);
    if (locked && drafts > 0 && !window.confirm(`"${module.title}" still has ${drafts} unpublished activit${drafts === 1 ? 'y' : 'ies'} that learners won't see.\n\nUnlock anyway?`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/programmes/${programmeId}/modules/${module.module_id}/lock`, { locked: !locked }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.locked ? `${module.title} locked for learners` : `${module.title} unlocked${res.data.learners_notified ? ' · learners notified' : ''}`);
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update lock');
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className={`flex items-center gap-2 h-8 px-2.5 rounded-md border text-xs cursor-pointer select-none transition-colors ${locked ? 'border-rose-500/40 bg-rose-500/10 text-rose-300' : 'border-zinc-700 text-zinc-300'}`} data-testid={`module-lock-toggle-${module.module_id}`}>
      {locked ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
      {locked ? 'Locked' : 'Unlocked'}
      <Switch checked={!locked} disabled={busy} onCheckedChange={toggle} className="scale-75" data-testid={`module-lock-switch-${module.module_id}`} />
    </label>
  );
}

export default function ModuleCard({ module, color, unit, programmeId, token, collapsed, onToggle, onReload, onPublish, onDelete, onMove, isFirst, isLast, children }) {
  const [editing, setEditing] = useState(false);
  const total = module.activity_count || 0;
  const published = module.published_count || 0;
  const allPublished = total > 0 && published === total;

  return (
    <section className="rounded-xl border border-zinc-800 bg-zinc-950/40" data-testid={`module-card-${module.module_id}`}>
      <div className="flex flex-wrap items-center gap-3 p-4 border-l-4 rounded-t-xl" style={{ borderLeftColor: color }}>
        <button type="button" onClick={onToggle} className="flex items-center gap-2 min-w-0 flex-1 text-left" data-testid={`module-toggle-${module.module_id}`}>
          <ChevronRight className={`h-4 w-4 text-zinc-400 transition-transform ${collapsed ? '' : 'rotate-90'}`} />
          <Layers className="h-4 w-4 shrink-0" style={{ color }} />
          <span className="min-w-0">
            <span className="block text-white font-semibold truncate">{module.title}</span>
            <span className="block text-xs text-zinc-500">
              {module.week_count} {unit.toLowerCase()}{module.week_count === 1 ? '' : 's'} · {total} activities
              {module.start_date ? ` · ${module.start_date}${module.end_date ? ` → ${module.end_date}` : ''}` : ''}
            </span>
          </span>
        </button>
        <Badge className={allPublished ? 'bg-green-500/15 text-green-400' : 'bg-amber-500/15 text-amber-300'} data-testid={`module-publish-status-${module.module_id}`}>
          {published}/{total} published
        </Badge>
        <div className="flex items-center gap-1.5">
          <ModuleLockToggle module={module} programmeId={programmeId} token={token} onReload={onReload} />
          <Button size="sm" variant="outline" onClick={() => setEditing((v) => !v)} className="h-8 border-zinc-700 text-zinc-200" data-testid={`module-edit-overview-${module.module_id}`}>
            {editing ? 'Close overview' : 'Overview'}
          </Button>
          {!allPublished && (
            <Button size="sm" disabled={total === 0} onClick={() => onPublish(module, true)} className="h-8 bg-green-600 hover:bg-green-700 text-white" data-testid={`module-publish-btn-${module.module_id}`}>
              <Send className="h-3.5 w-3.5 mr-1" />Publish module
            </Button>
          )}
          {published > 0 && (
            <Button size="sm" variant="outline" onClick={() => onPublish(module, false)} className="h-8 border-zinc-700 text-zinc-300" data-testid={`module-unpublish-btn-${module.module_id}`}>
              <Undo2 className="h-3.5 w-3.5 mr-1" />Unpublish
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={!['draft', 'approved'].includes(module.notes_status)}
            onClick={() => downloadModuleNotes(programmeId, module.module_id, token).catch((e) => toast.error(e.message))}
            className="h-8 border-zinc-700 text-zinc-200"
            title={module.notes_status === 'generating' ? 'Study notes are being written…' : module.notes_status ? 'Download this module\'s study notes' : 'Generate study notes in the Study Notes tab (or publish the module)'}
            data-testid={`module-guide-pack-btn-${module.module_id}`}
          >
            <FileDown className="h-3.5 w-3.5 mr-1" />Study notes{module.notes_status === 'approved' ? ' ✓' : module.notes_status === 'generating' ? '…' : ''}
          </Button>
          <Button size="icon" variant="ghost" disabled={isFirst} onClick={() => onMove(-1)} className="h-8 w-8 text-zinc-400" title="Move up" data-testid={`module-up-${module.module_id}`}><ArrowUp className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" disabled={isLast} onClick={() => onMove(1)} className="h-8 w-8 text-zinc-400" title="Move down" data-testid={`module-down-${module.module_id}`}><ArrowDown className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" onClick={() => onDelete(module)} className="h-8 w-8 text-red-400 hover:text-red-300" title="Delete module (weeks are kept)" data-testid={`module-delete-${module.module_id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      {editing && <ModuleOverviewEditor module={module} programmeId={programmeId} token={token} onSaved={onReload} />}
      {!collapsed && <div className="p-3 sm:p-4 space-y-5">{children}</div>}
    </section>
  );
}
